// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Gestion des échelons d'organisation d'une licence (pays → région → agence),
// partagée par DEUX écrans qui posent la même question à des moments différents :
//
// - `LicensePortal.js` — le license_admin gère SA licence au quotidien ;
// - `AdminEnterpriseClients.js` — Evalens monte l'organisation d'un client **à
//   la livraison du contrat**, avant même que ce client ait un administrateur.
//
// La distinction tient à une seule prop : `licenseId`. Absente = « ma licence »
// (le serveur la déduit de l'appelant). Présente = la licence désignée, ce que
// seul un admin/dev Evalens a le droit de faire.
//
// ⚠️ Extrait de `LicensePortal.js` le 28/07/2026 par déplacement verbatim
// (`ordonnerArbre`, `OrgUnitModal`, le tableau) : dupliquer la modale aurait
// fait diverger sa logique de niveaux acceptables, la partie la plus subtile.
import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';
import { Pencil, X, Plus, Trash2 } from 'lucide-react';
import {
  getLicenseOrgUnits, createLicenseOrgUnit, updateLicenseOrgUnit, deleteLicenseOrgUnit,
} from '../services/api';
import SortableFilterableTable from './SortableFilterableTable';

// Doit rester dans cet ordre et identique à `NIVEAUX_ORG` (flask_app.py) : le
// serveur refuse un échelon plus large sous un plus fin, et l'interface s'en
// sert pour ne proposer que des hiérarchies acceptables.
export const NIVEAUX_ORG = ['pays', 'region', 'agence'];

// Le serveur trie par PROFONDEUR puis par nom : toutes les agences se suivent,
// quel que soit leur parent. Indenter sur cette liste dessinerait un arbre faux
// — une agence lyonnaise apparaîtrait sous une région parisienne. On reconstruit
// donc l'ordre par parcours en profondeur, frères triés par nom.
export const ordonnerArbre = (unites) => {
  const enfants = new Map();
  unites.forEach(u => {
    const cle = u.parent_id || '';
    if (!enfants.has(cle)) enfants.set(cle, []);
    enfants.get(cle).push(u);
  });
  enfants.forEach(liste => liste.sort((a, b) => a.nom.localeCompare(b.nom)));

  const sortie = [];
  const vus = new Set();
  const descendre = (parentId, profondeur) => {
    (enfants.get(parentId) || []).forEach(u => {
      if (vus.has(u.id)) return;   // garde-fou : jamais de boucle infinie
      vus.add(u.id);
      sortie.push({ ...u, profondeur });
      descendre(u.id, profondeur + 1);
    });
  };
  descendre('', 0);
  // Un échelon dont le parent manquerait resterait invisible : on le rattache
  // à la racine plutôt que de le perdre en silence.
  unites.forEach(u => { if (!vus.has(u.id)) sortie.push({ ...u, profondeur: 0 }); });
  return sortie;
};

// Libellé indenté d'un échelon dans une liste déroulante, arbre déjà ordonné.
export const optionsEchelons = (orgUnits, t) => ordonnerArbre(orgUnits).map(u => (
  <option key={u.id} value={u.id}>
    {`${'— '.repeat(u.profondeur)}${u.nom} (${t(`org_level_${u.niveau}`)})`}
  </option>
));

const selectStyle = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 };
const champStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' };
const listeStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' };

function OrgUnitModal({ unit, orgUnits, onSave, onClose }) {
  const { t } = useI18n();
  const edition = !!unit;
  const [nom, setNom] = useState(unit?.nom || '');
  const [niveau, setNiveau] = useState(unit?.niveau || 'agence');
  const [parentId, setParentId] = useState(unit?.parent_id || '');
  const [quota, setQuota] = useState(unit?.pool_analyses_count ?? '');
  const [saving, setSaving] = useState(false);

  // Le serveur refuse un échelon plus large que son parent (400). On ne
  // propose donc que les niveaux réellement acceptables : l'utilisateur ne
  // peut pas construire une hiérarchie que le serveur rejettera.
  const parent = (orgUnits || []).find(u => u.id === parentId);
  const niveauxPossibles = parent
    ? NIVEAUX_ORG.slice(NIVEAUX_ORG.indexOf(parent.niveau) + 1)
    : NIVEAUX_ORG;

  const choisirParent = (id) => {
    setParentId(id);
    const p = (orgUnits || []).find(u => u.id === id);
    const possibles = p ? NIVEAUX_ORG.slice(NIVEAUX_ORG.indexOf(p.niveau) + 1) : NIVEAUX_ORG;
    if (!possibles.includes(niveau)) setNiveau(possibles[possibles.length - 1]);
  };

  const save = async () => {
    setSaving(true);
    // En édition, seuls le nom et le quota partent : le rattachement n'est pas
    // modifiable (déplacer un échelon invaliderait le chemin de tous ses
    // descendants, donc tous les cumuls).
    const payload = edition
      ? { nom, pool_analyses_count: quota === '' ? null : parseInt(quota, 10) }
      : { nom, niveau, parent_id: parentId || null,
          pool_analyses_count: quota === '' ? null : parseInt(quota, 10) };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{edition ? t('org_edit') : t('org_create')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('org_name')}</label>
          <input type="text" value={nom} onChange={e => setNom(e.target.value)} autoFocus style={champStyle} />
        </div>

        {!edition && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('org_parent')}</label>
              <select value={parentId} onChange={e => choisirParent(e.target.value)} style={listeStyle}>
                <option value="">{t('org_parent_root')}</option>
                {/* Une agence ne peut rien porter (dernier niveau) : elle n'est
                    jamais proposée comme parent. */}
                {ordonnerArbre(orgUnits || []).filter(u => u.niveau !== 'agence').map(u => (
                  <option key={u.id} value={u.id}>{`${'— '.repeat(u.profondeur)}${u.nom} (${t(`org_level_${u.niveau}`)})`}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('org_level')}</label>
              <select value={niveau} onChange={e => setNiveau(e.target.value)} style={listeStyle}>
                {niveauxPossibles.map(n => <option key={n} value={n}>{t(`org_level_${n}`)}</option>)}
              </select>
            </div>
          </>
        )}
        {edition && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>{t('org_parent_locked')}</div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('org_quota')}</label>
          <input type="number" min="0" value={quota} onChange={e => setQuota(e.target.value)} style={champStyle} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{t('org_quota_hint')}</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
          <button onClick={save} disabled={saving || !nom.trim()}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? '…' : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Onglet complet de gestion des échelons.
 *
 * `licenseId` — omis par le license_admin (sa licence), fourni par un admin
 * Evalens pour désigner la licence d'un client.
 * `onChanged` — notifié après toute création/modification/suppression, pour que
 * l'écran parent recharge sa propre copie de la liste (filtres, formulaire de
 * rattachement d'un siège) : deux copies rechargées ensemble restent cohérentes.
 */
export default function OrganisationEchelons({ licenseId, onChanged }) {
  const { t } = useI18n();
  const [orgUnits, setOrgUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [erreur, setErreur] = useState('');
  // `null` = arbre indenté ; dès qu'une colonne est triée, la vue bascule à
  // plat — l'indentation mentirait sur l'ordre.
  const [sort, setSort] = useState(null);

  const charger = useCallback(async () => {
    try {
      setOrgUnits((await getLicenseOrgUnits(licenseId)).org_units || []);
    } catch (e) { setErreur(e.message || t('err_generic')); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async (payload) => {
    setErreur('');
    try {
      if (editUnit) await updateLicenseOrgUnit(editUnit.id, payload, licenseId);
      else await createLicenseOrgUnit(payload, licenseId);
      setShowModal(false);
      setEditUnit(null);
      await charger();
      if (onChanged) onChanged();
    } catch (e) { setErreur(e.message || t('err_generic')); }
  };

  const supprimer = async (unit) => {
    if (!window.confirm(t('org_delete_confirm'))) return;
    setErreur('');
    try {
      await deleteLicenseOrgUnit(unit.id, licenseId);
      await charger();
      if (onChanged) onChanged();
    } catch (e) {
      // Le 409 « échelon non vide » porte déjà un message rédigé pour
      // l'utilisateur : le remonter tel quel vaut mieux qu'un texte générique.
      setErreur(e.message || t('err_generic'));
    }
  };

  const nomsParId = new Map(orgUnits.map(u => [u.id, u.nom]));
  const decorer = (u) => {
    // ⚠️ Le tri porte sur le RANG hiérarchique, pas sur le libellé : trier
    // « Type » sur le texte affiché donnait l'ordre alphabétique (Agence,
    // Pays, Région), qui ne veut rien dire. Croissant = du plus englobant au
    // plus fin. Un niveau inconnu part en fin de liste, pas en tête.
    const rang = NIVEAUX_ORG.indexOf(u.niveau);
    return {
      ...u,
      niveau_label: t(`org_level_${u.niveau}`),
      niveau_rang: rang === -1 ? NIVEAUX_ORG.length : rang,
      parent_nom: u.parent_id ? (nomsParId.get(u.parent_id) || '—') : '—',
    };
  };
  const aPlat = !!sort;
  const lignes = aPlat
    ? orgUnits.map(u => decorer({ ...u, profondeur: 0 }))
    : ordonnerArbre(orgUnits).map(decorer);

  return (
    <div>
      {erreur && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          {erreur}<button onClick={() => setErreur('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><X size={14} /></button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {aPlat ? (
          <button onClick={() => setSort(null)}
            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            {t('org_back_to_tree')}
          </button>
        ) : <span />}
        <button onClick={() => { setEditUnit(null); setShowModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <Plus size={14} />{t('org_create')}
        </button>
      </div>

      <SortableFilterableTable
        hideSearch
        rows={lignes}
        emptyLabel={t('org_empty')}
        onSortChange={setSort}
        columns={[
          {
            key: 'nom', label: t('org_name'),
            render: u => (
              <span style={{ paddingLeft: u.profondeur * 18 }}>
                {u.profondeur > 0 && <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>└</span>}
                <span style={{ fontWeight: 600 }}>{u.nom}</span>
              </span>
            ),
          },
          { key: 'niveau_rang', label: t('org_level'), render: u => u.niveau_label },
          ...(aPlat ? [{ key: 'parent_nom', label: t('org_parent') }] : []),
          { key: 'nb_membres', label: t('org_members'), align: 'right' },
          {
            key: 'pool_used', label: t('org_usage'), align: 'right',
            render: u => {
              const pct = u.pool_analyses_count ? Math.round((u.pool_used / u.pool_analyses_count) * 100) : null;
              return u.pool_analyses_count != null ? (
                <span style={{ color: pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : 'var(--black)', fontWeight: pct >= 80 ? 700 : 400 }}>
                  {u.pool_used} / {u.pool_analyses_count}
                </span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>{u.pool_used} · {t('org_quota_none')}</span>
              );
            },
          },
          {
            key: 'actions', label: '', sortable: false,
            render: u => (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                <button onClick={() => { setEditUnit(u); setShowModal(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 11 }}>
                  <Pencil size={10} />{t('ent_edit')}
                </button>
                <button onClick={() => supprimer(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #ef4444', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#ef4444' }}>
                  <Trash2 size={10} />{t('ent_delete')}
                </button>
              </div>
            ),
          },
        ]}
      />

      {showModal && (
        <OrgUnitModal unit={editUnit} orgUnits={orgUnits} onSave={enregistrer}
          onClose={() => { setShowModal(false); setEditUnit(null); }} />
      )}
    </div>
  );
}

/** Liste déroulante d'échelons, réutilisée par les formulaires d'invitation. */
export function SelecteurEchelon({ orgUnits, value, onChange, style }) {
  const { t } = useI18n();
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={style || selectStyle}>
      <option value="">{t('portal_seat_org_none')}</option>
      {optionsEchelons(orgUnits || [], t)}
    </select>
  );
}
